import {
  InputDialogType,
  StudyType,
  StudyProblem,
  StudyQuestion,
  StudyQuestionData,
  StudyQuestionChoiceData,
  StudyAudioInfoType,
  StudyQuestionMemoType,
} from '@/models/studyModel'
import { RawLocation } from 'vue-router'
import { AudioViewTypes, AudioViewSetting } from '@/const/AudioViewTypes'
import { Grades } from '@/const/Grades'
import { Subjects } from '@/const/Subjects'
import { ChoiceLabels } from '@/const/ChoiceLabels'
import { AnswerFormats, AnswerFormat, AnswerFormatName } from '@/const/AnswerFormats'
import { SUnitCategorySetting } from '@/const/ApiSettings'

import {
  ApiResponseLoadProblems,
  QuestionType,
  QuestionSelectChoices,
  ApiStudyAnswer,
} from '@/models/apiResponses/drill'

export class StudyUtils {
  /**
   * 学習表示種別を取得
   * @param apiResponse API受信した値
   * @return 学習表示種別
   */
  public static getStudyType(apiResponse: ApiResponseLoadProblems): StudyType {
    if (!apiResponse) return null

    return apiResponse.studyType
  }

  /**
   * 縦書きフラグを取得
   * @param apiResponse API受信した値
   * @return 縦書きフラグ
   */
  public static getIsVertical(apiResponse: ApiResponseLoadProblems): boolean {
    if (!apiResponse) return false

    return apiResponse.subjectCode === Subjects.JAPANESE.code && !apiResponse.isKoHorizontal
  }

  /**
   * 最終大問フラグを取得
   * @param apiResponse API受信した値
   * @return 最終大問フラグ
   */
  public static getIsLastProblem(apiResponse: ApiResponseLoadProblems): boolean {
    if (!apiResponse) return false

    return apiResponse.isLastProblem
  }

  /**
   * テスト名を生成を取得
   * @param apiResponse API受信した値
   * @return テスト名
   */
  public static buildStudyTitle(apiResponse: ApiResponseLoadProblems): string {
    if (!apiResponse) return ''

    return apiResponse.studyContentName
  }

  /**
   * 時間強制終了フラグを取得
   * @param apiResponse API受信した値
   * @return 時間強制終了フラグ
   */
  public static getEndConditionIsTime(apiResponse: ApiResponseLoadProblems): boolean {
    if (!apiResponse) return false

    return apiResponse.endConditionIsTime
  }

  /**
   * 経過時間（秒）を取得
   * @param apiResponse API受信した値
   * @return 経過時間（秒）
   */
  public static getDurationTotal(apiResponse: ApiResponseLoadProblems): number {
    if (!apiResponse) return 0

    const totalTime = apiResponse.sProblems.reduce((sum, sProblem) => {
      return (
        sum +
        sProblem.questions.reduce((sum, answer) => {
          return sum + answer.duration
        }, 0)
      )
    }, 0)

    return totalTime
  }

  /**
   * 制限時間（秒）を取得
   * @param apiResponse API受信した値
   * @return 制限時間（秒）
   */
  public static getTimeLimitTotal(apiResponse: ApiResponseLoadProblems): number {
    if (!apiResponse) return 0

    return apiResponse.timeLimitTotal
  }

  /**
   * 音声コメント画像URLを取得
   * @param apiResponse API受信した値
   * @return 音声コメント画像URL
   */
  public static getAudioCommentImageUrl(apiResponse: ApiResponseLoadProblems): string {
    if (!apiResponse) return ''

    return apiResponse.listeningCommentImageUrl
  }

  /**
   * 音声表示設定を取得
   * @param apiResponse API受信した値
   * TODO: apiResponse引数の型と変換処理
   * TODO: 音声表示設定
   */
  public static getAudioViewSetting(apiResponse: ApiResponseLoadProblems): AudioViewSetting {
    if (!apiResponse) return AudioViewTypes.VT1.setting

    return AudioViewTypes.VT1.setting
  }

  /**
   * 学年から数式キーボード名を取得
   * @param apiResponse API受信した値
   * TODO: apiResponse引数の型と変換処理
   */
  public static getMathKeyboardName(apiResponse: ApiResponseLoadProblems): string {
    if (!apiResponse) return ''

    return Grades.T1.mathKeyboardName
  }

  /**
   * 初回フラグを取得
   * @param apiResponse API受信した値
   * @returns 初回フラグ
   */
  public static getIsFirstTime(apiResponse: ApiResponseLoadProblems): boolean {
    if (!apiResponse) return false

    return apiResponse.isFirstTime
  }

  /**
   * 学習全問題情報を生成する
   * @param apiResponse API受信した値
   * @return 学習全問題情報
   */
  public static buildProblems(apiResponse: ApiResponseLoadProblems): StudyProblem[] {
    if (!apiResponse) return []

    const studyProblems: StudyProblem[] = apiResponse.sProblems.map((sProblem) => {
      return {
        id: sProblem.id,
        problemNumber: sProblem.sortNum,
        imageUrls: sProblem.imageUrls.map((str) => str),
        questions: sProblem.questions.map((question) => {
          return {
            // 設問ID
            id: question.id,
            // 設問番号テキスト
            questionNumberText: this.buildQuestionNumberText(question.sortNum, question.labelText),
            // 指示文
            indicativeText: question.indicativeText,
            // 入力形式
            answerFormat: this.findAnswerFormat(question.answerFormat),
            // テキスト、テキストエリア、手書き、キーボード入力形式で使用する問題情報
            questionDatas: this.buildQuestionInputDatas(question),
            // 単一選択、複数選択、並び替え入力形式で使用する問題情報
            questionChoiceData: this.buildQuestionChoiceData(question),
            // 画像URL
            imageUrls: question.imageUrls.map((url) => url),
            // 経過時間
            duration: question.duration,
          }
        }),
        duration: 0,
      }
    })

    return studyProblems
  }

  /**
   * 定義済み入力形式取得
   * @param answerFormatName 解答形式名
   * @return 定義済み入力形式
   */
  private static findAnswerFormat(answerFormatName: AnswerFormatName): AnswerFormat {
    const answerFormatConstName = Object.getOwnPropertyNames(AnswerFormats).find(
      (AnswerFormatConstName) => AnswerFormats[AnswerFormatConstName].name === answerFormatName
    )
    return answerFormatConstName ? AnswerFormats[answerFormatConstName] : AnswerFormats['text']
  }

  /**
   * 選択系解答形式「単一選択、複数選択 並び替え」判定
   * @param answerFormatName 解答形式名
   * @return 選択系解答形式判定
   */
  private static isChoiceAnswerFormat(answerFormatName: AnswerFormatName): boolean {
    return [AnswerFormats.SINGLE_CHOICE.name, AnswerFormats.MULTI_CHOICE.name, AnswerFormats.SORT_CHOICE.name].includes(
      answerFormatName
    )
  }

  /**
   * 解答入力系設問一覧
   * @param question APIから受け取った設問一覧
   * @return 解答入力系設問一覧
   */
  private static buildQuestionInputDatas(question: QuestionType): StudyQuestionData[] | undefined {
    if (this.isChoiceAnswerFormat(question.answerFormat)) return undefined

    return question.questionPulldowns.length
      ? // プルダウン設問一覧
        question.questionPulldowns.map((questionPulldown, index) => {
          return {
            inputValue: question.inputs[index],
            beforeText: questionPulldown.beforeText,
            afterText: questionPulldown.afterText,
            selectItems: [{ value: '', label: '' }].concat(
              questionPulldown.choiceTexts.map((pulldownChoiceText) => {
                return {
                  value: pulldownChoiceText.choiceText,
                  label: pulldownChoiceText.choiceText,
                }
              })
            ),
          }
        })
      : // 入力設問一覧
        question.questionTexts.map((questionText, index) => {
          return {
            inputValue: question.inputs[index] || '',
            beforeText: questionText.beforeText,
            afterText: questionText.afterText,
            inputType: this.buildQuestionInputType(question, index),
            lang: question.conversionType,
            savedStrokes: question.strokeData[index],
          }
        })
  }

  /**
   * 解答入力系設問の解答入力値反映
   * @param question APIから受け取った設問一覧
   * @param index 設問中出題番号
   * @return 解答入力系設問の解答入力値
   */
  private static buildQuestionInputType(question: QuestionType, index: number): string {
    if (question.isHandInputs.length === 0) {
      return InputDialogType.TEGAKI
    } else {
      return question.isHandInputs[index] ? InputDialogType.TEGAKI : InputDialogType.KEYBOARD
    }
  }

  /**
   * 解答選択系設問一覧
   * @param question APIから受け取った設問一覧
   * @return 解答選択系設問一覧
   */
  private static buildQuestionChoiceData(question: QuestionType): StudyQuestionChoiceData | undefined {
    if (!this.isChoiceAnswerFormat(question.answerFormat) || !question.questionSelectChoices) return undefined

    const questionChoices: StudyQuestionChoiceData = {
      inputValue: this.buildQuestionChoiceInputValue(question),
      choiceItems: question.questionSelectChoices.map((questionSelectChoice) => {
        return {
          value:
            // valueは並び替え時にはchoiceText、それ以外はsortNum
            question.answerFormat === AnswerFormats.SORT_CHOICE.name
              ? questionSelectChoice.choiceText
              : String(questionSelectChoice.sortNum),
          label: questionSelectChoice.labelText || ChoiceLabels.DEFAULT_KANA_LABELS[questionSelectChoice.sortNum - 1],
          choiceImage: questionSelectChoice.imageUrl,
          choiceText: questionSelectChoice.choiceText,
        }
      }),
      // TODO: 仕様条件を確認
      labelNotDisplayable: this.isLabelNotDisplayable(question.questionSelectChoices),
      // TODO: 仕様条件を確認
      canDeselectedOrder: false,
    }

    return questionChoices
  }

  /**
   * 解答選択系設問の解答入力値反映
   * @param question APIから受け取った設問一覧
   * @return 選択系設問の解答入力値
   */
  private static buildQuestionChoiceInputValue(question: QuestionType): StudyQuestionChoiceData['inputValue'] {
    if (question.answerFormat === AnswerFormats.SINGLE_CHOICE.name) {
      return question.inputs[0] || ''
    } else {
      return question.inputs
    }
  }

  /**
   * 単一選択、複数選択時のラベル非表示判定
   * @param questionSelectChoices APIから受け取った選択形式問題要素
   * @return ラベル表示不可判定
   */
  private static isLabelNotDisplayable(questionSelectChoices: QuestionSelectChoices[]): boolean {
    // ラベル非表示キーワードが設問群に一つでも含まれている場合、設問全てのラベルを非表示にする
    const emptyLabelText = questionSelectChoices.find(
      (questionSelectChoice) => questionSelectChoice.labelText === process.env.VUE_APP_EMPTY_QUESTION_LABEL_WORD
    )
    return !!emptyLabelText
  }

  /**
   * 設問表示ラベル生成
   * @param sortNum 設問出題番号
   * @param labelText 設問指定ラベル
   * @return 設問表示ラベル
   */
  private static buildQuestionNumberText(sortNum: number, labelText: string | null): string {
    const label = labelText ? labelText : `(${sortNum})`
    return label === process.env.VUE_APP_EMPTY_QUESTION_LABEL_WORD ? '' : label
  }

  /**
   * 音声情報を生成する
   * @param apiResponse API受信した値
   * @return 音声情報
   */
  public static buildAudioInfos(apiResponse: ApiResponseLoadProblems): StudyAudioInfoType[] {
    if (!apiResponse) return []

    const studyAudios: StudyAudioInfoType[] = apiResponse.sProblems
      .map((sProblem) => {
        const problemAudios: StudyAudioInfoType[] = sProblem.audioUrls.map((audioUrl) => {
          return {
            url: audioUrl,
            problemId: sProblem.id,
          }
        })
        const questionAudios: StudyAudioInfoType[] = sProblem.questions
          .map((question) => {
            return question.audioUrls.map((audioUrl) => {
              return {
                url: audioUrl,
                questionId: question.id,
              }
            })
          })
          .flat()

        return [...problemAudios, ...questionAudios]
      })
      .flat()

    return studyAudios
  }

  /**
   * 学習中のメモ情報一覧を生成する
   * @param apiResponse API受信した値
   * @returns 学習中のメモ情報一覧
   */
  public static buildQuestionMemos(apiResponse: ApiResponseLoadProblems): StudyQuestionMemoType[] {
    const problemTypes = apiResponse.sProblems
    if (!apiResponse || apiResponse.sProblems.length === 0) return []

    const studyQuestionMemos: StudyQuestionMemoType[] = problemTypes.flatMap((problem) =>
      problem.questions
        .filter((question) => question.drawingData)
        .map((question) => ({
          questionId: question.id,
          src: '',
          drawingData: question.drawingData,
        }))
    )

    return studyQuestionMemos
  }

  /**
   * 学習後の戻り先Locationを生成する
   * @param apiResponse API受信した値
   * @returns 戻り先Location
   */
  public static buildStudyEndMoveLocation(apiResponse: ApiResponseLoadProblems): RawLocation | null {
    if (!apiResponse) return null

    switch (apiResponse.sUnitCategory) {
      case SUnitCategorySetting.LAST_MINUTE_CHECK:
        return {
          name: 'StudentStudyResultLastMinuteCheck',
          params: { sUnitId: String(apiResponse.sUnitId) },
        }
      case SUnitCategorySetting.RESOLVE:
        return {
          name: 'StudentStudyResultResolve',
          params: { sUnitId: String(apiResponse.sUnitId) },
        }
    }
    return null
  }

  /**
   * 学習送信用の設問単位解答情報を生成する
   * @param resultDrillId 学習中のドリル実施結果ID
   * @param studyProblem 学習中の大問情報
   * @param studyQuestion 学習中の設問情報
   * @param questionMemo 学習中のメモ情報
   * @return 学習送信用の設問単位解答情報
   */
  public static buildQuestionStudyAnswer(
    resultDrillId: number,
    studyProblem: StudyProblem,
    studyQuestion: StudyQuestion,
    questionMemo: StudyQuestionMemoType | null
  ): ApiStudyAnswer {
    const studyAnswer = {
      resultDrillId: resultDrillId,
      studyAnswers: [
        {
          sProblemId: studyProblem.id,
          questionId: studyQuestion.id,
          answerFormatName: studyQuestion.answerFormat.name,
          duration: studyQuestion.duration,
          inputs: this.buildQuestionStudyAnswerInputs(studyQuestion),
          isHandInputs: this.buildQuestionStudyAnswerInputType(studyQuestion),
          strokeData: this.buildQuestionStudyAnswerStrokeData(studyQuestion),
        },
      ],
      questionDrawing: {
        questionId: questionMemo && questionMemo.drawingData ? questionMemo.questionId : null,
        drawingData: questionMemo?.drawingData || null,
      },
    }
    return studyAnswer
  }

  /**
   * 学習送信用の大問単位解答情報を生成する
   * @param resultDrillId 学習中のドリル実施結果ID
   * @param studyProblem 学習中の大問情報
   * @param questionMemo 学習中のメモ情報
   * @return 学習送信用の大問単位解答情報
   */
  public static buildProblemStudyAnswer(
    resultDrillId: number,
    studyProblem: StudyProblem,
    questionMemo: StudyQuestionMemoType | null
  ): ApiStudyAnswer {
    const studyAnswer = {
      resultDrillId: resultDrillId,
      studyAnswers: studyProblem.questions.map((studyQuestion) => {
        return {
          sProblemId: studyProblem.id,
          questionId: studyQuestion.id,
          answerFormatName: studyQuestion.answerFormat.name,
          duration: studyQuestion.duration,
          inputs: this.buildQuestionStudyAnswerInputs(studyQuestion),
          isHandInputs: this.buildQuestionStudyAnswerInputType(studyQuestion),
          strokeData: this.buildQuestionStudyAnswerStrokeData(studyQuestion),
        }
      }),
      questionDrawing: {
        questionId: questionMemo && questionMemo.drawingData ? questionMemo.questionId : null,
        drawingData: questionMemo?.drawingData || null,
      },
    }
    return studyAnswer
  }

  /**
   * 学習送信用の複数大問単位解答情報を生成する
   * @param resultDrillId 学習中のドリル実施結果ID
   * @param studyProblems 学習中の大問情報一覧
   * @param questionMemo 学習中のメモ情報
   * @return 学習送信用の大問単位解答情報
   */
  public static buildProblemsStudyAnswer(
    resultDrillId: number,
    studyProblems: StudyProblem[],
    questionMemo: StudyQuestionMemoType | null
  ): ApiStudyAnswer {
    const studyAnswer = {
      resultDrillId: resultDrillId,
      studyAnswers: studyProblems
        .map((studyProblem) => {
          return studyProblem.questions.map((studyQuestion) => {
            return {
              sProblemId: studyProblem.id,
              questionId: studyQuestion.id,
              answerFormatName: studyQuestion.answerFormat.name,
              duration: studyQuestion.duration,
              inputs: this.buildQuestionStudyAnswerInputs(studyQuestion),
              isHandInputs: this.buildQuestionStudyAnswerInputType(studyQuestion),
              strokeData: this.buildQuestionStudyAnswerStrokeData(studyQuestion),
            }
          })
        })
        .flat(),
      questionDrawing: {
        questionId: questionMemo && questionMemo.drawingData ? questionMemo.questionId : null,
        drawingData: questionMemo?.drawingData || null,
      },
    }
    return studyAnswer
  }

  /**
   * 学習送信用の設問解答入力値一覧を生成する
   * @param studyQuestion 学習中の設問情報
   * @return 設問解答入力値
   */
  private static buildQuestionStudyAnswerInputs(studyQuestion: StudyQuestion): string[] {
    return this.isChoiceAnswerFormat(studyQuestion.answerFormat.name)
      ? this.buildQuestionStudyChoiceAnswerInputValues(studyQuestion)
      : this.buildQuestionStudyAnswerInputValues(studyQuestion)
  }

  /**
   * 学習送信用の入力系設問解答値一覧を生成する
   * @param studyQuestion 学習中の設問情報
   * @return 入力系設問解答値
   */
  private static buildQuestionStudyAnswerInputValues(studyQuestion: StudyQuestion): string[] {
    if (!studyQuestion.questionDatas) return ['']

    return studyQuestion.questionDatas.map((questionData) => questionData.inputValue)
  }

  /**
   * 学習送信用の選択系設問解答値一覧を生成する
   * @param studyQuestion 学習中の設問情報
   * @return 選択系設問解答値
   */
  private static buildQuestionStudyChoiceAnswerInputValues(studyQuestion: StudyQuestion): string[] {
    if (!studyQuestion.questionChoiceData) return ['']

    return typeof studyQuestion.questionChoiceData.inputValue === 'string'
      ? [studyQuestion.questionChoiceData.inputValue]
      : studyQuestion.questionChoiceData.inputValue
  }

  /**
   * 学習送信用の手書き入力判定一覧を生成する
   * @param studyQuestion 学習中の設問情報
   * @return 学習中の手書き入力判定一覧
   */
  private static buildQuestionStudyAnswerInputType(studyQuestion: StudyQuestion): boolean[] {
    if (!studyQuestion.questionDatas) return []

    return studyQuestion.questionDatas.map((questionData) => {
      return questionData.inputType === InputDialogType.TEGAKI
    })
  }

  /**
   * 学習送信用の手書き筆跡座標一覧を生成する
   * @param studyQuestion 学習中の設問情報
   * @return 学習送信用の手書き筆跡座標一覧
   */
  private static buildQuestionStudyAnswerStrokeData(studyQuestion: StudyQuestion): QuestionType['strokeData'] {
    if (!studyQuestion.questionDatas) return []

    return studyQuestion.questionDatas.map((questionData) => questionData.savedStrokes || [])
  }
}
